import { datadogRum, RumInitConfiguration } from '@datadog/browser-rum'
import { configureAuth, type UniversalAuthConfig } from '@lastbrand/fe-common/universal-auth'
import '@lastbrand/web-ui/styles.css'
import '@src/globals.css'
import '@src/SectionRenderer/sections/gridSlider/gridSlider.scss'
import '@src/quince-components/src/atoms/swiper/swiper.scss'
import '@src/quince-site-integrated/src/components/FormElements/inputStyles.scss'
import '@src/quince-site-integrated/src/components/FormElements/passwordStyles.scss'
import '@src/pages/contact-us/contact-us-components/glady-help-center/gladly-help-center-external.scss'
import '@src/SectionRenderer/sections/heroBanner/corporateGiftingV2Hero.scss'
import '@src/quince-styles/scss/index.scss' // THIS IS INTENTIONAL DO NOT CHANGE THE ORDER!!!!! THIS SHOULD ALWAYS BE THE LAST IMPORT
import { AppProps } from 'next/app'
import Head from 'next/head'
import { appWithTranslation, useTranslation } from 'next-i18next'
import { useEffect, useState, useRef } from 'react'
import { isMobile } from 'react-device-detect'

import { QueryClient, QueryClientProvider, HydrationBoundary, type DehydratedState } from '@lastbrand/sdk-http'
import { DD_RUM_APP_ID, DD_RUM_ENABLED_FLAG, DD_RUM_CLIENT_TOKEN, DD_RUM_SERVICE } from '@src/config/env'
import getAmplifyConfig from '@src/modules/auth/amplify/amplify-config'
import authConfigDataHolder from '@src/modules/auth/auth-config.holder'
import { mapIdentityConfigToAuthConfig } from '@src/modules/auth/identity-config-api'
import { readRawIdentityConfigCache } from '@src/modules/auth/identity-config-cache'
import { initializeClientEntry } from '@src/modules/client-entry'
import {
  getUserAgnosticGlobalFeatureGatesFromMemory,
  hydrateUserAgnosticGlobalFeatureGatesFromNextData,
  setUserAgnosticGlobalFeatureGatesInMemory,
} from '@src/modules/global-state/user-agnostic-feature-gates.browser'
import { CookieBanner } from '@src/quince-components/src'
import { FEATURES, MODULES } from '@src/quince-site-common/src/constants/template-info'
import { getIsNativeViewClient } from '@src/quince-site-common/src/helpers/getIsNativeViewClient'
import { useOnRouteUpdateWithStateFlush } from '@src/quince-site-common/src/hooks/useOnRouteUpdateWithStateFlush'
import pdpStateManager from '@src/quince-site-common/src/redux/globalStateManager/PDPStateManager'
import isBrowser from '@src/quince-site-common/src/utils/isBrowser'
import { captureException } from '@src/quince-site-common/src/utils/sentryUtil'
import { getCookie } from '@src/quince-site-integrated/src/utils'
import { ServerSideBaseProps } from '@src/server-side/api/api.types'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'
import { initializeFeCommon } from '@src/utils/feCommonInitialize'
import { getRegionIdFromLocale } from '@src/utils/general.utils'
import { initMSW } from '@src/utils/mockServiceWorkerInitalize'

import nextI18nextConfig from '../../next-i18next.config'

if (DD_RUM_ENABLED_FLAG && DD_RUM_APP_ID && DD_RUM_CLIENT_TOKEN) {
  const datadogConfig: RumInitConfiguration = {
    applicationId: DD_RUM_APP_ID,
    clientToken: DD_RUM_CLIENT_TOKEN,
    // `site` refers to the Datadog site parameter of your organization
    // see https://docs.datadoghq.com/getting_started/site/
    site: 'datadoghq.com',
    service: DD_RUM_SERVICE,
    env: process.env.NEXT_PUBLIC_DD_RUM_ENV,
    // Specify a version number to identify the deployed version of your application in Datadog
    version: process.env.NEXT_PUBLIC_RELEASE_VERSION,
    sessionSampleRate: 25,
    // All of those 1% sessions will include session replay
    sessionReplaySampleRate: 1,
    traceSampleRate: 25,
    telemetrySampleRate: 1,
    // @i18n-ignore
    defaultPrivacyLevel: 'mask-user-input',
    // Enable tracking of user interactions, resources, and long tasks (required for Clickmaps)
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    // TODO: Uncomment this when we have a way to trigger session-replay programmatically, Now Session Replay starts recording automatically when calling init()
    // startSessionReplayRecordingManually: true, // Allow to do session-replay programmatically if needed
    allowedTracingUrls: [
      // @i18n-ignore
      { match: 'https://api-prod-public.onequince.com/', propagatorTypes: ['tracecontext'] },
      // @i18n-ignore
      { match: 'https://api-qa.onequince.com/', propagatorTypes: ['tracecontext'] },
    ],
  }
  // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
  datadogRum.init(datadogConfig)
  // console.log('Datadog initialized with configuration', datadogConfig)
}

if (isBrowser()) {
  hydrateUserAgnosticGlobalFeatureGatesFromNextData()
  const onAuthError = (error: Error, context: Record<string, unknown>) => {
    captureException(error, {
      module: MODULES.UNIVERSAL_AUTH,
      feature: FEATURES.UNIVERSAL_AUTH,
      extraInfo: context,
    })
  }
  if (getUserAgnosticGlobalFeatureGatesFromMemory().multi_pool_auth_enabled) {
    const cachedIdentity = readRawIdentityConfigCache()
    if (cachedIdentity) {
      void configureAuth({
        ...mapIdentityConfigToAuthConfig(cachedIdentity),
        onError: onAuthError,
      } as UniversalAuthConfig)
    }
  } else {
    void configureAuth({ ...getAmplifyConfig(), onError: onAuthError } as UniversalAuthConfig)
  }
}

initializeFeCommon()

type AppPageProps = ServerSideBaseProps & {
  dehydratedState?: DehydratedState
  isB2BLoginPage?: boolean
}

function MyApp({ Component, pageProps, router }: AppProps<AppPageProps>) {
  const { t } = useTranslation('common')
  // const legacyUniversalAuthConfiguredRef = useRef(false)
  const queryClientRef = useRef<QueryClient | null>(null)
  if (queryClientRef.current === null) {
    queryClientRef.current = new QueryClient({
      defaultOptions: {
        queries: { refetchOnWindowFocus: false },
      },
    })
  }
  const queryClient = queryClientRef.current
  const dehydratedState: DehydratedState | undefined = pageProps.dehydratedState
  const [mswReady, setMswReady] = useState(process.env.NEXT_PUBLIC_ENABLE_MSW !== 'true')
  const isNativeView = getIsNativeViewClient()
  // Route prefix covers all /b2b/* on the main domain; isB2BLoginPage covers the
  // /log-in route when served from the B2B self-serve domain (business.quince.com).
  const isB2BMode = router.pathname.startsWith('/b2b') || Boolean(pageProps.isB2BLoginPage)
  useOnRouteUpdateWithStateFlush([pdpStateManager], pageProps)
  setUserAgnosticGlobalFeatureGatesInMemory(pageProps.userAgnosticGlobalFeatureGates)

  // Initialize MSW before rendering
  useEffect(() => {
    if (process.env.NEXT_PUBLIC_ENABLE_MSW === 'true') {
      initMSW()
        .then(() => {
          setMswReady(true)
        })
        .catch(() => {
          setMswReady(true)
        })
    }
  }, [])

  useEffect(() => {
    if (pageProps.userAgnosticGlobalFeatureGates.multi_pool_auth_enabled) {
      const regionId = getRegionIdFromLocale(pageProps.locale, pageProps.regionDetails)
      void authConfigDataHolder.initialize(regionId)
    }
  }, [pageProps.userAgnosticGlobalFeatureGates, pageProps.locale, pageProps.regionDetails])

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
    initializeClientEntry(pageProps.userAgnosticGlobalFeatureGates)
    const isNewUserInner = getCookie('new-user-created') === 'random' && getCookie('_lb_user_first_access') === 'true'

    if (isNewUserInner) {
      trackEvent(TELEMETRY_EVENT_NAMES.NEW_USER_CREATED_RANDOM, {})
    }
  }, [pageProps.userAgnosticGlobalFeatureGates])

  if (!mswReady) {
    return null
  }

  return (
    <QueryClientProvider client={queryClient}>
      <HydrationBoundary state={dehydratedState}>
        <Head>
          <meta
            name="viewport"
            // @i18n-ignore
            content={`width=device-width, initial-scale=1${getIsNativeViewClient() || isMobile ? ', maximum-scale=1' : ''}`}
          />
        </Head>
        <nav aria-label={t('accessibility_link')}>
          <a className="sr-only" href="/accessibility">
            {t('view_our_accessibility_statement')}
          </a>
        </nav>
        <Component {...pageProps} />
        {!isNativeView && !isB2BMode ? <CookieBanner /> : null}
      </HydrationBoundary>
    </QueryClientProvider>
  )
}

export default appWithTranslation(MyApp, nextI18nextConfig)
